import { Subject, of } from 'rxjs';
import { omit, isEqual, includes } from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { catchError, finalize, takeUntil } from 'rxjs/operators';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { AbstractControlOptions, FormBuilder, FormGroup, Validators } from '@angular/forms';

import { ValidationHelper } from '@diktup/frontend/helpers';

import { SharedService } from '../../services';
import { AuthService } from '../../services/auth/auth.service';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

@Component({
  template: '',
})
export class BaseLoginComponent implements OnInit {
  private unsubscribeAll: Subject<any> = new Subject<any>();

  activeId = 1;
  showAlert = false;
  recoverPwd = false;
  posId: string = '';
  signInForm: FormGroup;
  signUpForm: FormGroup;
  authenticated: boolean;
  isButtonDisabled = false;
  forgotPasswordForm: FormGroup;
  isResetButtonDisabled: boolean;

  constructor(
    protected router: Router,
    protected activeModal: NgbModal,
    protected authService: AuthService,
    protected formBuilder: FormBuilder,
    protected translate: TranslateService,
    protected toastrService: ToastrService,
    protected sharedService: SharedService,
    protected changeDetectorRef: ChangeDetectorRef,
  ) {
    this.sharedService.posId$.pipe(takeUntil(this.unsubscribeAll)).subscribe((posId) => {
      this.posId = posId;
    });
    this.authService.authenticated$.pipe(takeUntil(this.unsubscribeAll)).subscribe((authenticated: boolean) => {
      this.authenticated = authenticated;
      this.changeDetectorRef.markForCheck();
    });
  }

  ngOnInit(): void {
    this.forgotPasswordForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
    });
    const initialValues = this.forgotPasswordForm.value;
    this.forgotPasswordForm.valueChanges.subscribe((ivalues) => {
      this.isResetButtonDisabled = isEqual(ivalues, initialValues);
    });
    this.signInForm = this.formBuilder.group({
      login: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required],
      rememberMe: [''],
    });
    this.signUpForm = this.formBuilder.group({
      firstName: ['', [Validators.required]],
      lastName: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      pwd: this.formBuilder.group(
        {
          password: ['', Validators.required],
          confirmPassword: ['', Validators.required],
        },
        { validators: [ValidationHelper.matchingFields('password', 'confirmPassword')] } as AbstractControlOptions,
      ),
    });
  }

  signIn(): void {
    this.showAlert = false;
    this.isButtonDisabled = true;
    this.authService
      .loginForTarget(this.signInForm.value)
      .pipe(
        catchError(() => {
          this.isButtonDisabled = false;
          this.translate.get('SHARED.SOMETHING_WENT_WRONG').subscribe((text: string) => {
            this.toastrService.error(text);
          });
          this.changeDetectorRef.markForCheck();
          return of(null);
        }),
      )
      .subscribe((res) => {
        if (res?.data || res?.[0]?.data) {
          this.router.navigate(['/']);
          this.isButtonDisabled = false;
          this.changeDetectorRef.markForCheck();
        }
        if (res?.errors) {
          const error: string = res?.errors[0]?.extensions?.exception?.response?.error;
          if (includes(error, 'password')) {
            this.translate.get('SHARED.PASSWORD_INCORRECT').subscribe((text: string) => {
              this.toastrService.error(text);
            });
          } else if (includes(error, 'email') || includes(error, 'login')) {
            this.translate.get('SHARED.NO_USER_WITH_LOGIN').subscribe((text: string) => {
              this.toastrService.error(text);
            });
          } else {
            this.translate.get('SHARED.SOMETHING_WENT_WRONG').subscribe((text: string) => {
              this.toastrService.error(text);
            });
          }
          this.isButtonDisabled = false;
          this.signInForm.enable();
          this.signInForm.reset();
          this.showAlert = true;
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  signUp() {
    this.isButtonDisabled = true;
    if (this.signUpForm.invalid) {
      return;
    }
    const formValue: any = {
      ...omit(this.signUpForm.value, 'pwd'),
      password: this.signUpForm.value.pwd.password,
    };
    this.authService
      .registerForTarget(formValue)
      .pipe(
        catchError((error) => {
          const exception = error?.graphQLErrors[0].extensions?.exception?.response?.error?.response?.error;
          this.isButtonDisabled = false;
          if (includes(exception, 'already exists')) {
            this.translate.get('SHARED.USER_EXISTS').subscribe((text: string) => {
              this.toastrService.error(text);
            });
          } else {
            this.translate.get('SHARED.SOMETHING_WENT_WRONG').subscribe((text: string) => {
              this.toastrService.error(text);
            });
          }
          this.changeDetectorRef.markForCheck();
          return of(null);
        }),
      )
      .subscribe((res: any) => {
        if (res?.errors?.length) {
          this.isButtonDisabled = false;
          this.signUpForm.enable();
          this.signUpForm.reset();
        } else if (res) {
          this.router.navigate(['/']);
          this.isButtonDisabled = false;
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  sendResetLink(): void {
    if (this.forgotPasswordForm.invalid) {
      return;
    }
    this.forgotPasswordForm.disable();
    this.showAlert = false;
    this.authService
      .forgotPassword(this.posId, this.forgotPasswordForm.get('email')?.value)
      .pipe(
        finalize(() => {
          this.forgotPasswordForm.enable();
          this.forgotPasswordForm.reset();
          this.showAlert = true;
        }),
        catchError(() => {
          this.translate.get('SHARED.NO_USER_WITH_EMAIL').subscribe((text: string) => {
            this.toastrService.error(text);
          });
          this.changeDetectorRef.markForCheck();
          return of(null);
        }),
      )
      .subscribe((response) => {
        if (response) {
          this.translate.get('SHARED.PASSWORD_RESET_SENT').subscribe((text: string) => {
            this.toastrService.success(text);
          });
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  forgotPwd() {
    this.activeId = 3;
    this.recoverPwd = true;
  }

  closeModal() {
    this.activeModal.dismissAll();
  }

  ngOnDestroy(): void {
    this.unsubscribeAll.next(true);
    this.unsubscribeAll.complete();
  }
}
